/* eslint-disable max-lines */
import { Col, Progress, Row, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Card } from 'src/components/Card/Card';
import { DataView } from 'src/components/DataView/DataView';
import { LoanApplicationStatusView } from 'src/components/LoanApplicationStatusView/LoanApplicationStatusView';
import { Table } from 'src/components/Table/Table';
import { LoanApplicationDto, TransactionDto } from 'src/generated-api-client';
import { AgentLoanApplicationsStore } from 'src/stores/AgentLoanApplicationsStore/AgentLoanApplicationsStore';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

type AgentLoanApplicaitonRepaymentsTabProps = {
    application?: LoanApplicationDto;
};

export const AgentLoanApplicaitonRepaymentsTab = observer(
    ({ application }: AgentLoanApplicaitonRepaymentsTabProps) => {
        const { t } = useTranslation();

        useEffect(() => {
            application?.id &&
                AgentLoanApplicationsStore.loadTransactions(application.id);

            return () => {
                AgentLoanApplicationsStore.transactionsLoader.reset();
            };
        }, [application?.number]);

        return (
            <div>
                <Card
                    title={t(
                        'App.Components.LoanApplicationRepaymentsTab.RepaymentsProgress.Title',
                        'Repayment Progress',
                    )}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24}>
                            <StyledPercentValue level={4}>
                                {t(
                                    'App.Components.LoanApplicationRepaymentsTab.RepaymentsProgress.Value',
                                    '{{percentage}} % of loan repaid',
                                    {
                                        percentage:
                                            application?.aggregatingFigures
                                                ?.repaidPercentage,
                                    },
                                )}
                            </StyledPercentValue>
                            <Progress
                                percent={
                                    application?.aggregatingFigures
                                        ?.repaidPercentage
                                }
                                showInfo={false}
                            />
                        </Col>
                        <Col xs={24} sm={12}>
                            <DataView
                                label={t(
                                    'App.Components.LoanApplicationRepaymentsTab.RepaymentsProgress.RepaidAmount',
                                    'Repaid amount',
                                )}
                                value={
                                    application?.aggregatingFigures
                                        ?.repaidAmount
                                }
                                formatValue="currency-sar"
                            />
                        </Col>
                        <Col xs={24} sm={12}>
                            <StyledDataView
                                label={t(
                                    'App.Components.LoanApplicationRepaymentsTab.RepaymentsProgress.OutstandingAmount',
                                    'Outstanding amount',
                                )}
                                value={
                                    application?.aggregatingFigures
                                        ?.outstandingAmount
                                }
                                formatValue="currency-sar"
                            />
                        </Col>
                    </Row>
                </Card>
                <Card>
                    <StyledTable
                        loading={
                            AgentLoanApplicationsStore.transactionsLoader
                                .isLoading
                        }
                        columns={columns}
                        dataSource={AgentLoanApplicationsStore.transactions}
                        pagination={false}
                    />
                </Card>
            </div>
        );
    },
);

const formNames = FormHelper.createNameProvider<TransactionDto>();

const columns: ColumnType<TransactionDto>[] = [
    {
        key: 'createdAt',
        dataIndex: formNames.getPath('createdAt'),
        title: <Trans i18nKey="App.Transactions.Columns.CreatedAt" />,
        render: (value: string) => {
            return <DataView value={value} formatValue="dateTime" />;
        },
    },
    {
        key: 'date',
        dataIndex: formNames.getPath('date'),
        title: <Trans i18nKey="App.Transactions.Columns.Date" />,
        render: (value: string) => {
            return <DataView value={value} formatValue="dateTime" />;
        },
    },
    {
        key: 'number',
        dataIndex: formNames.getPath('number'),
        title: <Trans i18nKey="App.Transactions.Columns.Number" />,
        render: (value: string) => {
            return <DataView value={value} />;
        },
    },
    {
        key: 'deductedAmount',
        dataIndex: formNames.getPath('deductedAmount'),
        title: <Trans i18nKey="App.Transactions.Columns.DeductedAmount" />,
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'collectionCommissionAmount',
        dataIndex: formNames.getPath('collectionCommissionAmount'),
        title: (
            <Trans i18nKey="App.Transactions.Columns.CollectionCommissionAmount" />
        ),
        render: (value: string) => {
            return <DataView value={value} formatValue="currency-sar" />;
        },
    },
    {
        key: 'status',
        dataIndex: formNames.getPath('status'),
        title: <Trans i18nKey="App.Transactions.Columns.Status" />,
        render: (value: string) => {
            return (
                <DataView
                    value={value}
                    formatValue={(value) => (
                        <LoanApplicationStatusView status={value} />
                    )}
                />
            );
        },
    },
];

const StyledPercentValue = styled(Typography.Title)`
    font-size: 24px;
    font-weight: 600;
    text-align: center;
`;

const StyledTable = styled(Table)`
    .ant-table-content {
        overflow-x: auto;
    }

    .ant-table-thead > tr > th {
        white-space: nowrap;
    }

    .ant-table-tbody > tr > td {
        white-space: nowrap;
    }

    .ant-pagination-options {
        width: auto !important;
    }
`;

const StyledDataView = styled(DataView)`
    .data-view__label {
        text-align: right;
    }

    .data-view__value {
        text-align: right;
    }
`;
